import React, { useState, useEffect } from "react";
import "./adminEvent.css";
import { createEvent, updateEvent } from "./adminEventService";
import { fetchActiveSpeakers } from "../services/adminSpeakerServices";

const AdminEventForm = ({ event, onSaved, onCancel }) => {

 /**
 * =========================
 * DATE HELPERS
 * =========================
 */

 const extractDateFromUTC = (utcString) => {
 try {
 if (!utcString) return "";
 const date = new Date(utcString);
 const offset = date.getTimezoneOffset();
 const local = new Date(date.getTime() - offset * 60000);
 return local.toISOString().slice(0, 10); // YYYY-MM-DD
 } catch (error) {
 return "";
 }
 };

 const extractTimeFromUTC = (utcString) => {
 try {
 if (!utcString) return "";
 const date = new Date(utcString);
 const offset = date.getTimezoneOffset();
 const local = new Date(date.getTime() - offset * 60000);
 return local.toISOString().slice(11, 16); // HH:mm
 } catch (error) {
 return "";
 }
 };

 const combineDateTimeToUTC = (dateStr, timeStr) => {
 try {
 if (!dateStr || !timeStr) return null;
 const localDate = new Date(`${dateStr}T${timeStr}:00`);
 return localDate.toISOString();
 } catch (error) {
 return null;
 }
 };

 /**
 * =========================
 * INITIAL STATE
 * =========================
 */

 const [formData, setFormData] = useState({
 title: event?.title || "",
 description: event?.description || "",
 eventDate: extractDateFromUTC(event?.eventStartTime),
 eventStartTime: extractTimeFromUTC(event?.eventStartTime),
 eventDuration: event?.eventDuration || 90,
 category: event?.category || "Workshop",
 level: event?.level || "All Levels",
 originalPrice: event?.originalPrice || 499,
 price: event?.price || 299,
 language: event?.language || "English",
 platform: event?.platform || "Zoom",
 highlights: event?.highlights ? event.highlights.join('\n') : "",
 maxParticipants: event?.maxParticipants || 50,
 speakerId: event?.speakerId || "",
 cancelled: event?.cancelled ?? false
 });

 /**
 * =========================
 * SPEAKERS STATE
 * =========================
 */
 const [speakers, setSpeakers] = useState([]);
 const [selectedSpeaker, setSelectedSpeaker] = useState(null);

 useEffect(() => {
 const loadSpeakers = async () => {
 try {
 const response = await fetchActiveSpeakers();
 if (response.status === 200) {
 setSpeakers(response.data);
 // Pre-select speaker if editing
 if (event?.speakerId) {
 const found = response.data.find(s => s.id === event.speakerId);
 if (found) setSelectedSpeaker(found);
 }
 }
 } catch (error) {
 }
 };
 loadSpeakers();
 }, [event?.speakerId]);

 const handleSpeakerChange = (e) => {
 const speakerId = e.target.value;
 setFormData({ ...formData, speakerId });
 if (speakerId) {
 const found = speakers.find(s => s.id === parseInt(speakerId));
 setSelectedSpeaker(found || null);
 } else {
 setSelectedSpeaker(null);
 }
 };

 const handleChange = (e) => {
 const { name, value, type } = e.target;
 
 setFormData({
 ...formData,
 [name]: type === 'number' ? (value === '' ? '' : Number(value)) : value,
 });
 };

 const handleHighlightsChange = (e) => {
 setFormData({
 ...formData,
 highlights: e.target.value
 });
 };

 /**
 * =========================
 * SUBMIT
 * =========================
 */

 const handleSubmit = async (e) => {
 e.preventDefault();

 const highlightsArray = formData.highlights
 .split('\n')
 .map(h => h.trim())
 .filter(h => h.length > 0);

 const payload = {
 title: formData.title,
 description: formData.description,
 eventStartTime: combineDateTimeToUTC(formData.eventDate, formData.eventStartTime),
 eventDuration: parseInt(formData.eventDuration),
 category: formData.category,
 level: formData.level,
 speakerId: formData.speakerId ? parseInt(formData.speakerId) : null,
 originalPrice: parseFloat(formData.originalPrice),
 price: parseFloat(formData.price),
 language: formData.language,
 platform: formData.platform,
 highlights: highlightsArray,
 maxParticipants: parseInt(formData.maxParticipants),
 cancelled: formData.cancelled ?? false
 };

 let response;

 if (event?.id) {
 response = await updateEvent(event.id, payload);
 } else {
 response = await createEvent(payload);
 }

 if (response.status === 200 || response.status === 201) {
 onSaved();
 } else {
 alert(response.message);
 }
 };

 return (

 <div className="form-card">

 <div className="form-header">
 <h2>{event ? "Edit Event" : "Create Event"}</h2>
 </div>

 <form onSubmit={handleSubmit} className="form-grid enhanced-admin-form">

 {/* Basic Information */}
 <div className="form-section">
 <h3 className="section-title">📝 Basic Information</h3>
 
 <div className="form-row">
 <div className="form-group">
 <label>Event Title *</label>
 <input
 name="title"
 value={formData.title}
 onChange={handleChange}
 placeholder="e.g. Advanced Public Speaking Masterclass"
 required
 />
 </div>

 <div className="form-group">
 <label>Category *</label>
 <select
 name="category"
 value={formData.category}
 onChange={handleChange}
 required
 >
 <option value="Workshop">Workshop</option>
 <option value="Masterclass">Masterclass</option>
 <option value="Bootcamp">Bootcamp</option>
 <option value="Seminar">Seminar</option>
 <option value="Training">Training</option>
 </select>
 </div>
 </div>

 <div className="form-row">
 <div className="form-group">
 <label>Level *</label>
 <select
 name="level"
 value={formData.level}
 onChange={handleChange}
 required
 >
 <option value="Beginner">Beginner</option>
 <option value="Intermediate">Intermediate</option>
 <option value="Advanced">Advanced</option>
 <option value="All Levels">All Levels</option>
 </select>
 </div>

 <div className="form-group">
 <label>Language *</label>
 <select
 name="language"
 value={formData.language}
 onChange={handleChange}
 required
 >
 <option value="English">English</option>
 <option value="Hindi">Hindi</option>
 <option value="Spanish">Spanish</option>
 <option value="French">French</option>
 <option value="German">German</option>
 <option value="Mandarin">Mandarin</option>
 <option value="Japanese">Japanese</option>
 </select>
 </div>
 </div>

 <div className="form-group full-width">
 <label>Description *</label>
 <textarea
 name="description"
 value={formData.description}
 onChange={handleChange}
 placeholder="Detailed description of the event content and what participants will learn..."
 rows={4}
 required
 />
 </div>
 </div>

 {/* Schedule Information */}
 <div className="form-section">
 <h3 className="section-title">📅 Schedule Information</h3>
 
 <div className="form-row">
 <div className="form-group">
 <label>Event Date *</label>
 <input
 type="date"
 name="eventDate"
 value={formData.eventDate}
 onChange={handleChange}
 required
 />
 </div>

 <div className="form-group">
 <label>Start Time *</label>
 <input
 type="time"
 name="eventStartTime"
 value={formData.eventStartTime}
 onChange={handleChange}
 required
 />
 </div>
 </div>

 <div className="form-row">
 <div className="form-group">
 <label>Duration (minutes) *</label>
 <input
 type="number"
 name="eventDuration"
 value={formData.eventDuration}
 onChange={handleChange}
 min="30"
 max="480"
 required
 />
 </div>

 <div className="form-group">
 <label>Platform *</label>
 <select
 name="platform"
 value={formData.platform}
 onChange={handleChange}
 required
 >
 <option value="Zoom">Zoom</option>
 <option value="Google Meet">Google Meet</option>
 <option value="Microsoft Teams">Microsoft Teams</option>
 <option value="In-Person">In-Person</option>
 </select>
 </div>
 </div>
 </div>

 {/* Speaker Information */}
 <div className="form-section">
 <h3 className="section-title">👨🏫 Speaker Information</h3>
 
 <div className="form-row">
 <div className="form-group full-width">
 <label>Select Speaker *</label>
 <select
 name="speakerId"
 value={formData.speakerId}
 onChange={handleSpeakerChange}
 required
 >
 <option value="">Select Speaker</option>
 {speakers.map((speaker) => (
 <option key={speaker.id} value={speaker.id}>
 {speaker.name}
 </option>
 ))}
 </select>
 </div>
 </div>

 {selectedSpeaker && (
 <div className="speaker-preview">
 <div className="speaker-preview-header">
 {selectedSpeaker.avatarUrl && (
 <img src={selectedSpeaker.avatarUrl} alt={selectedSpeaker.name} className="speaker-avatar" />
 )}
 <div>
 <div className="speaker-preview-name">{selectedSpeaker.name}</div>
 {selectedSpeaker.bio && (
 <div className="speaker-preview-bio">{selectedSpeaker.bio}</div>
 )}
 </div>
 </div>
 </div>
 )}
 </div>

 {/* Pricing & Capacity */}
 <div className="form-section">
 <h3 className="section-title">💰 Pricing & Capacity</h3>
 
 <div className="form-row">
 <div className="form-group">
 <label>Original Price (₹) *</label>
 <input
 type="number"
 name="originalPrice"
 value={formData.originalPrice}
 onChange={handleChange}
 min="0"
 step="0.01"
 required
 />
 </div>

 <div className="form-group">
 <label>Discounted Price (₹) *</label>
 <input
 type="number"
 name="price"
 value={formData.price}
 onChange={handleChange}
 min="0"
 step="0.01"
 required
 />
 </div>
 </div>

 <div className="form-row">
 <div className="form-group">
 <label>Max Participants *</label>
 <input
 type="number"
 name="maxParticipants"
 value={formData.maxParticipants}
 onChange={handleChange}
 min="1"
 max="500"
 required
 />
 </div>

 <div className="form-group">
 <label>Savings Amount</label>
 <input
 type="text"
 value={`₹${(parseFloat(formData.originalPrice || 0) - parseFloat(formData.price || 0)).toFixed(2)}`}
 disabled
 className="calculated-field"
 />
 </div>
 </div>
 </div>

 {/* Event Highlights */}
 <div className="form-section">
 <h3 className="section-title">✨ Event Highlights</h3>
 
 <div className="form-group full-width">
 <label>Key Highlights (One per line)</label>
 <textarea
 value={formData.highlights}
 onChange={handleHighlightsChange}
 placeholder={`Interactive exercises and real-time practice\nPersonalized feedback from expert coaches\nCertificate of completion included\nAccess to exclusive speaking resources\nLifetime community access`}
 rows={6}
 />
 <small className="field-help">Enter each highlight on a new line. These will be displayed as bullet points.</small>
 </div>
 </div>

 <div className="form-actions full-width">
 <button type="submit" className="btn primary enhanced-submit">
 <span className="btn-icon">💾</span>
 {event ? "Update Event" : "Create Event"}
 </button>

 <button
 type="button"
 className="btn secondary"
 onClick={onCancel}
 >
 <span className="btn-icon">❌</span>
 Cancel
 </button>
 </div>

 </form>

 </div>

 );

};

export default AdminEventForm;